import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { Trans } from "react-i18next";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationStatCard } from "web_myplann/Inspiration/Components/InspirationStatCard";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import { EARLY_FREEDOM_EXAMPLE } from "web_myplann/Inspiration/Utils/earlyFreedomMath";

const R = Resources.Inspiration.EarlyFreedom;

export const EarlyFreedomColdIntro: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const formatAmount = useFormatGoalAmount();

	const cardBg = theme.palette.background.paper;

	const money = (value: number) => formatAmount(value, INSPIRATION_CURRENCY);

	return (
		<>
			<Stack bgcolor={cardBg} px={1.6} py={2}>
				<Typography
					variant="body1"
					color={theme.palette.grey[700]}
					lineHeight={theme.lineHeight.relaxed}
				>
					<Trans
						i18nKey={getResourcePath(R.intro)}
						components={{
							b: (
								<Typography component="span" variant="body1" fontWeight={600} />
							),
						}}
					/>
				</Typography>
			</Stack>

			<Typography variant="sectionLabel">{t(R.exampleTitle)}</Typography>

			<Stack bgcolor={cardBg} px={1.6} py={1.6} gap={1.2}>
				<Typography variant="body2" color={theme.palette.grey[600]}>
					<Trans
						i18nKey={getResourcePath(R.exampleBody)}
						values={{
							spend: money(EARLY_FREEDOM_EXAMPLE.spend),
							rent: money(EARLY_FREEDOM_EXAMPLE.rent),
							gap: money(EARLY_FREEDOM_EXAMPLE.gap),
							capital: money(EARLY_FREEDOM_EXAMPLE.capital),
						}}
						components={{
							b: <Typography component="span" variant="bodyStrong" tabular />,
							orange: (
								<Typography
									component="span"
									variant="bodyStrong"
									color={theme.palette.orange.main}
									tabular
								/>
							),
							blue: (
								<Typography
									component="span"
									variant="bodyStrong"
									color={theme.palette.blue.main}
									tabular
								/>
							),
						}}
					/>
				</Typography>

				<Stack direction="row" gap={1.2}>
					<InspirationStatCard
						label={t(R.exampleTodayLabel)}
						amount={money(EARLY_FREEDOM_EXAMPLE.rent)}
						bg={theme.palette.surface.muted}
						labelColor={theme.palette.grey[500]}
						amountColor={theme.palette.green.main}
					/>
					<InspirationStatCard
						label={t(R.exampleNeedLabel)}
						amount={`~${money(EARLY_FREEDOM_EXAMPLE.capital)}`}
						bg={theme.palette.blue[50]}
						labelColor={theme.palette.blue.main}
						amountColor={theme.palette.blue.main}
					/>
				</Stack>

				<Typography
					variant="caption"
					color={theme.palette.grey[500]}
					textAlign="center"
				>
					{t(R.exampleHint)}
				</Typography>
			</Stack>
		</>
	);
};
